import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertTriangle, TrendingUp, TrendingDown, Users, CheckCircle, XCircle, Activity, Upload, X, Plus, Trash2, ChevronLeft, ChevronRight, Clock, UserPlus, UserMinus, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import '../styles/modern.css';

// Get API base URL - works with both localhost and external access
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ? `${import.meta.env.VITE_API_BASE_URL}/api` : '/api';

interface DnuAnalytics {
  overview: {
    totalCarriers: number;
    activeCarriers: number;
    removedCarriers: number;
    carriersAddedThisMonth: number;
    carriersRemovedThisMonth: number;
    totalViolations: number;
    complianceRate: number;
  };
  topViolators: Array<{
    dotNumber: number;
    carrierName: string;
    dnuDate: string;
    violationCount: number;
    latestViolation: string;
  }>;
  monthlyTimeline: Array<{
    month: string;
    added: number;
    removed: number;
    violations: number;
  }>;
  recentActivity: Array<{
    dotNumber: number;
    carrierName: string;
    action: 'added' | 'removed' | 'violation';
    date: string;
  }>;
}

interface DnuEntry {
  id: number;
  carrierLegalName: string | null;
  dotNumber: number;
  mcNumber: string | null;
  shipper: string;
  dnuDate: string;
  listFile: string;
  notes: string | null;
  removedDate: string | null;
}

export const DnuAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<DnuAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shipper, setShipper] = useState('USPS');
  const [availableShippers, setAvailableShippers] = useState<string[]>(['USPS', 'FedEx', 'UPS', 'Amazon']);

  // DNU Entries state
  const [dnuEntries, setDnuEntries] = useState<DnuEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entriesTotal, setEntriesTotal] = useState(0);
  const [entriesPage, setEntriesPage] = useState(0);
  const entriesPerPage = 50;

  // Sorting state for DNU Carrier List
  const [sortField, setSortField] = useState<'dotNumber' | 'carrierLegalName' | 'mcNumber' | 'dnuDate' | 'removedDate' | 'status'>('dnuDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Add Carrier modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addFormData, setAddFormData] = useState({
    dot_number: '',
    carrier_name: '',
    mc_number: '',
    dnu_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importShipper, setImportShipper] = useState('USPS');
  const [importDnuDate, setImportDnuDate] = useState(new Date().toISOString().split('T')[0]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: boolean; message: string; imported?: number; errors?: string[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchAnalytics();
    fetchAvailableShippers();
  }, [shipper]);

  useEffect(() => {
    fetchDnuEntries();
  }, [shipper, entriesPage]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/dnu/analytics?shipper=${shipper}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to fetch analytics`);
      }

      const data = await response.json();
      setAnalytics(data.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchDnuEntries = async () => {
    try {
      setEntriesLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/dnu/entries?shipper=${shipper}&limit=${entriesPerPage}&offset=${entriesPage * entriesPerPage}`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDnuEntries(data.data || []);
        setEntriesTotal(data.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch DNU entries:', err);
    } finally {
      setEntriesLoading(false);
    }
  };

  const fetchAvailableShippers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/dnu/shippers`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.shippers) {
          setAvailableShippers(data.data.available || data.data.shippers);
        }
      }
    } catch (err) {
      console.error('Failed to fetch shippers:', err);
    }
  };

  const handleAddCarrier = async () => {
    if (!addFormData.dot_number || !addFormData.dnu_date) {
      setAddError('DOT Number and DNU Date are required');
      return;
    }

    try {
      setAddLoading(true);
      setAddError(null);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/dnu/entry`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          dot_number: addFormData.dot_number,
          shipper: shipper,
          dnu_date: addFormData.dnu_date,
          carrier_name: addFormData.carrier_name || null,
          mc_number: addFormData.mc_number || null,
          notes: addFormData.notes || null
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowAddModal(false);
        setAddFormData({
          dot_number: '',
          carrier_name: '',
          mc_number: '',
          dnu_date: new Date().toISOString().split('T')[0],
          notes: ''
        });
        fetchDnuEntries();
        fetchAnalytics();
      } else {
        setAddError(data.error || 'Failed to add carrier');
      }
    } catch (err: any) {
      setAddError(err.message || 'Failed to add carrier');
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveEntry = async (id: number) => {
    if (!window.confirm('Are you sure you want to mark this carrier as removed from DNU?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/dnu/entry/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (response.ok) {
        fetchDnuEntries();
        fetchAnalytics();
      }
    } catch (err) {
      console.error('Failed to remove entry:', err);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImportSubmit = async () => {
    if (!selectedFile) {
      setImportResult({ success: false, message: 'Please select a file first' });
      return;
    }

    try {
      setImporting(true);
      setImportResult(null);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('shipper', importShipper);
      formData.append('dnuDate', importDnuDate);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/dnu/upload`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setImportResult({
          success: true,
          message: `Imported: ${data.data.imported} new, ${data.data.removed || 0} removed, ${data.data.unchanged || 0} unchanged for ${data.data.shipper}`,
          imported: data.data.imported,
          errors: data.data.errors
        });

        if (importShipper === shipper) {
          await fetchAnalytics();
          await fetchDnuEntries();
        }

        setTimeout(() => {
          setSelectedFile(null);
          setShowImportModal(false);
          setImportResult(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }, 3000);
      } else {
        setImportResult({
          success: false,
          message: data.error || 'Failed to import DNU list'
        });
      }
    } catch (err: any) {
      setImportResult({
        success: false,
        message: err.message || 'Failed to import DNU list'
      });
    } finally {
      setImporting(false);
    }
  };

  const totalPages = Math.ceil(entriesTotal / entriesPerPage);

  if (loading) {
    return (
      <>
        <div className="container-spacious">
          <LoadingSpinner />
        </div>
      </>
    );
  }

  if (error || !analytics) {
    return (
      <>
        <div className="container-spacious">
          <div className="card-modern" style={{ background: '#f2dad2', border: '2px solid #b3412c' }}>
            <p style={{ color: '#7a2b1e' }}>{error || 'No analytics data available'}</p>
          </div>
        </div>
      </>
    );
  }

  const { overview, topViolators, monthlyTimeline, recentActivity } = analytics;

  return (
    <>
      <div className="container-spacious">
        {/* Header */}
        <div className="fade-in-up" style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h1 className="heading-1">DNU Analytics Dashboard</h1>
              <p className="text-muted">
                Do Not Use (DNU) compliance monitoring and analytics
              </p>
            </div>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#857f73', marginBottom: '4px' }}>
                  SHIPPER
                </label>
                <select
                  value={shipper}
                  onChange={(e) => { setShipper(e.target.value); setEntriesPage(0); }}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid #d3ccbc',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    minWidth: '120px',
                    cursor: 'pointer'
                  }}
                >
                  {availableShippers.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => setShowImportModal(true)}
                className="btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '20px' }}
              >
                <Upload size={16} />
                Import DNU List
              </button>
            </div>
          </div>
        </div>

        {/* Overview Stats Grid */}
        <div className="grid-spacious grid-cols-[repeat(auto-fit,minmax(200px,1fr))] fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div className="card-modern">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <Users size={20} style={{ color: 'var(--color-primary)' }} />
              <div className="stat-label">TOTAL CARRIERS</div>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>
              {overview.totalCarriers.toLocaleString()}
            </div>
          </div>

          <div className="card-modern">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <AlertTriangle size={20} style={{ color: '#b3412c' }} />
              <div className="stat-label">ACTIVE DNU</div>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#b3412c' }}>
              {overview.activeCarriers.toLocaleString()}
            </div>
          </div>

          <div className="card-modern">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <CheckCircle size={20} style={{ color: 'var(--color-success)' }} />
              <div className="stat-label">COMPLIANCE RATE</div>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--color-success)' }}>
              {overview.complianceRate.toFixed(1)}%
            </div>
            <div className="text-muted text-sm">
              {(overview.activeCarriers - overview.totalViolations).toLocaleString()} compliant
            </div>
          </div>

          <div className="card-modern">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <XCircle size={20} style={{ color: 'var(--color-danger)' }} />
              <div className="stat-label">VIOLATING CARRIERS</div>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--color-danger)' }}>
              {overview.totalViolations.toLocaleString()}
            </div>
            <div className="text-muted text-sm">
              Hauled after DNU date
            </div>
          </div>

          <div className="card-modern">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <TrendingUp size={20} style={{ color: 'var(--color-warning)' }} />
              <div className="stat-label">ADDED THIS MONTH</div>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--color-warning)' }}>
              {overview.carriersAddedThisMonth.toLocaleString()}
            </div>
          </div>

          <div className="card-modern">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <TrendingDown size={20} style={{ color: 'var(--color-success)' }} />
              <div className="stat-label">REMOVED THIS MONTH</div>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--color-success)' }}>
              {overview.carriersRemovedThisMonth.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Monthly Timeline Chart */}
        <div className="card-modern fade-in-up section-spacing" style={{ animationDelay: '0.2s' }}>
          <h2 className="heading-2" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.5rem' }}>
            <Activity style={{ color: 'var(--color-primary)' }} />
            12-Month Trend
          </h2>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={monthlyTimeline}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  background: '#fbf9f4',
                  border: '1px solid #e2ddd1',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px'
                }}
              />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              <Line
                type="monotone"
                dataKey="added"
                stroke="#b07d2b"
                strokeWidth={2}
                name="Carriers Added"
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="removed"
                stroke="#5d7148"
                strokeWidth={2}
                name="Carriers Removed"
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="violations"
                stroke="#b3412c"
                strokeWidth={2}
                name="Carriers Violating"
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top Violators Table */}
        <div className="card-modern fade-in-up section-spacing" style={{ animationDelay: '0.3s' }}>
          <h2 className="heading-2" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.5rem' }}>
            <AlertTriangle style={{ color: '#b3412c' }} />
            Top 10 Violators
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table className="table-modern">
              <thead>
                <tr>
                  <th style={{ width: '15%' }}>DOT Number</th>
                  <th style={{ width: '35%' }}>Carrier Name</th>
                  <th style={{ width: '15%' }}>DNU Date</th>
                  <th style={{ width: '15%', textAlign: 'center' }}>Violations</th>
                  <th style={{ width: '20%' }}>Latest Violation</th>
                </tr>
              </thead>
              <tbody>
                {topViolators.map((violator, index) => (
                  <tr
                    key={index}
                    onClick={() => navigate(`/carrier/${violator.dotNumber}`)}
                    style={{ cursor: 'pointer' }}
                    className="hover:bg-gray-50"
                  >
                    <td style={{ fontFamily: 'monospace', color: 'var(--color-primary)' }}>
                      {violator.dotNumber}
                    </td>
                    <td style={{ fontWeight: '600' }}>
                      {violator.carrierName}
                    </td>
                    <td>
                      {new Date(violator.dnuDate).toLocaleDateString()}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span
                        className="badge-modern"
                        style={{
                          background: violator.violationCount > 10 ? '#f2dad2' : '#f1e2c2',
                          color: violator.violationCount > 10 ? '#7a2b1e' : '#664911',
                          fontWeight: '700'
                        }}
                      >
                        {violator.violationCount}
                      </span>
                    </td>
                    <td>
                      {new Date(violator.latestViolation).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* DNU Carrier List */}
        <div className="card-modern fade-in-up section-spacing" style={{ animationDelay: '0.35s' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 className="heading-2" style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: 0 }}>
              <Users style={{ color: 'var(--color-primary)' }} />
              DNU Carrier List
              <span style={{ fontSize: '0.875rem', fontWeight: 'normal', color: '#857f73' }}>
                ({entriesTotal.toLocaleString()} total)
              </span>
            </h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Plus size={16} />
              Add Carrier
            </button>
          </div>
          
          <div style={{ overflowX: 'auto', maxHeight: '500px', overflowY: 'auto' }}>
            {entriesLoading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <LoadingSpinner />
              </div>
            ) : (
              <table className="table-modern">
                <thead style={{ position: 'sticky', top: 0, background: '#fbf9f4', zIndex: 1 }}>
                  <tr>
                    <th 
                      style={{ width: '12%', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        if (sortField === 'dotNumber') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('dotNumber');
                          setSortDirection('asc');
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        DOT Number
                        {sortField === 'dotNumber' ? (
                          sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                        ) : (
                          <ArrowUpDown size={14} style={{ opacity: 0.3 }} />
                        )}
                      </div>
                    </th>
                    <th 
                      style={{ width: '25%', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        if (sortField === 'carrierLegalName') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('carrierLegalName');
                          setSortDirection('asc');
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Carrier Name
                        {sortField === 'carrierLegalName' ? (
                          sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                        ) : (
                          <ArrowUpDown size={14} style={{ opacity: 0.3 }} />
                        )}
                      </div>
                    </th>
                    <th 
                      style={{ width: '12%', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        if (sortField === 'mcNumber') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('mcNumber');
                          setSortDirection('asc');
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        MC Number
                        {sortField === 'mcNumber' ? (
                          sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                        ) : (
                          <ArrowUpDown size={14} style={{ opacity: 0.3 }} />
                        )}
                      </div>
                    </th>
                    <th 
                      style={{ width: '12%', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        if (sortField === 'dnuDate') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('dnuDate');
                          setSortDirection('desc');
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        DNU Date
                        {sortField === 'dnuDate' ? (
                          sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                        ) : (
                          <ArrowUpDown size={14} style={{ opacity: 0.3 }} />
                        )}
                      </div>
                    </th>
                    <th 
                      style={{ width: '12%', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        if (sortField === 'removedDate') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('removedDate');
                          setSortDirection('desc');
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Removed Date
                        {sortField === 'removedDate' ? (
                          sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                        ) : (
                          <ArrowUpDown size={14} style={{ opacity: 0.3 }} />
                        )}
                      </div>
                    </th>
                    <th 
                      style={{ width: '12%', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        if (sortField === 'status') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('status');
                          setSortDirection('asc');
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Status
                        {sortField === 'status' ? (
                          sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                        ) : (
                          <ArrowUpDown size={14} style={{ opacity: 0.3 }} />
                        )}
                      </div>
                    </th>
                    <th style={{ width: '15%', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...dnuEntries].sort((a, b) => {
                    let aVal: any, bVal: any;
                    switch (sortField) {
                      case 'dotNumber':
                        aVal = a.dotNumber;
                        bVal = b.dotNumber;
                        break;
                      case 'carrierLegalName':
                        aVal = (a.carrierLegalName || '').toLowerCase();
                        bVal = (b.carrierLegalName || '').toLowerCase();
                        break;
                      case 'mcNumber':
                        aVal = a.mcNumber || '';
                        bVal = b.mcNumber || '';
                        break;
                      case 'dnuDate':
                        aVal = new Date(a.dnuDate).getTime();
                        bVal = new Date(b.dnuDate).getTime();
                        break;
                      case 'removedDate':
                        aVal = a.removedDate ? new Date(a.removedDate).getTime() : 0;
                        bVal = b.removedDate ? new Date(b.removedDate).getTime() : 0;
                        break;
                      case 'status':
                        aVal = a.removedDate ? 1 : 0;
                        bVal = b.removedDate ? 1 : 0;
                        break;
                      default:
                        return 0;
                    }
                    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
                    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
                    return 0;
                  }).map((entry) => (
                    <tr key={entry.id}>
                      <td style={{ fontFamily: 'monospace', color: 'var(--color-primary)' }}>
                        <a
                          href={`/carrier/${entry.dotNumber}`}
                          onClick={(e) => { e.preventDefault(); navigate(`/carrier/${entry.dotNumber}`); }}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          {entry.dotNumber}
                        </a>
                      </td>
                      <td style={{ fontWeight: '500' }}>
                        {entry.carrierLegalName || '-'}
                      </td>
                      <td style={{ fontFamily: 'monospace' }}>
                        {entry.mcNumber || '-'}
                      </td>
                      <td>
                        {new Date(entry.dnuDate).toLocaleDateString()}
                      </td>
                      <td>
                        {entry.removedDate ? new Date(entry.removedDate).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <span
                          className="badge-modern"
                          style={{
                            background: entry.removedDate ? '#d1e6d9' : '#f2dad2',
                            color: entry.removedDate ? '#4b5c3a' : '#7a2b1e',
                            fontWeight: '600'
                          }}
                        >
                          {entry.removedDate ? 'Removed' : 'Active'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {!entry.removedDate && (
                          <button
                            onClick={() => handleRemoveEntry(entry.id)}
                            style={{
                              background: '#f2dad2',
                              color: '#b3412c',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: 'var(--radius-md)',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}
                          >
                            <Trash2 size={14} />
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e2ddd1' }}>
              <div style={{ fontSize: '0.875rem', color: '#857f73' }}>
                Showing {entriesPage * entriesPerPage + 1} - {Math.min((entriesPage + 1) * entriesPerPage, entriesTotal)} of {entriesTotal.toLocaleString()}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setEntriesPage(p => Math.max(0, p - 1))}
                  disabled={entriesPage === 0}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid #d3ccbc',
                    background: entriesPage === 0 ? '#efebe1' : '#fff',
                    cursor: entriesPage === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <span style={{ padding: '8px 12px', fontSize: '0.875rem' }}>
                  Page {entriesPage + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setEntriesPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={entriesPage >= totalPages - 1}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid #d3ccbc',
                    background: entriesPage >= totalPages - 1 ? '#efebe1' : '#fff',
                    cursor: entriesPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="card-modern fade-in-up section-spacing" style={{ animationDelay: '0.4s' }}>
          <h2 className="heading-2" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.5rem' }}>
            <Clock style={{ color: 'var(--color-primary)' }} />
            Recent Activity
          </h2>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {recentActivity && recentActivity.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {recentActivity.slice(0, 20).map((activity, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '12px 16px',
                      background: '#f5f2ea',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate(`/carrier/${activity.dotNumber}`)}
                  >
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: activity.action === 'added' ? '#f1e2c2' : activity.action === 'removed' ? '#d1e6d9' : '#f2dad2'
                    }}>
                      {activity.action === 'added' ? (
                        <UserPlus size={18} style={{ color: 'var(--color-warning)' }} />
                      ) : activity.action === 'removed' ? (
                        <UserMinus size={18} style={{ color: 'var(--color-success)' }} />
                      ) : (
                        <AlertTriangle size={18} style={{ color: '#b3412c' }} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                        {activity.carrierName}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#857f73' }}>
                        DOT: {activity.dotNumber} • {activity.action === 'added' ? 'Added to DNU' : activity.action === 'removed' ? 'Removed from DNU' : 'Violation'}
                      </div>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#857f73' }}>
                      {new Date(activity.date).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#857f73' }}>
                No recent activity
              </div>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="card-modern fade-in-up section-spacing" style={{ animationDelay: '0.45s', background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-400)' }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
            <AlertTriangle size={20} style={{ color: 'var(--color-gray-700)', marginTop: '2px' }} />
            <div>
              <h3 style={{ fontWeight: '600', color: 'var(--color-gray-900)', marginBottom: '8px' }}>
                About DNU Compliance
              </h3>
              <p style={{ color: 'var(--color-gray-700)', fontSize: '0.875rem', lineHeight: '1.5' }}>
                The Do Not Use (DNU) list contains carriers that {shipper} has prohibited from hauling their shipments.
                A "violation" occurs when a carrier on the DNU list has an inspection record showing they transported {shipper} cargo after their DNU date.
                The compliance rate shows the percentage of DNU carriers that have not violated the restriction.
              </p>
            </div>
          </div>
        </div>

        {/* Add Carrier Modal */}
        {showAddModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: "var(--z-modal)",
              padding: '20px'
            }}
            onClick={() => !addLoading && setShowAddModal(false)}
          >
            <div
              className="card-modern"
              style={{
                maxWidth: '500px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="heading-2" style={{ margin: 0 }}>Add Carrier to DNU</h2>
                <button
                  onClick={() => !addLoading && setShowAddModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: '#857f73'
                  }}
                  disabled={addLoading}
                >
                  <X size={20} />
                </button>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  DOT Number *
                </label>
                <input
                  type="text"
                  value={addFormData.dot_number}
                  onChange={(e) => setAddFormData({ ...addFormData, dot_number: e.target.value })}
                  disabled={addLoading}
                  placeholder="e.g., 1234567"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid #d3ccbc',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  Carrier Name
                </label>
                <input
                  type="text"
                  value={addFormData.carrier_name}
                  onChange={(e) => setAddFormData({ ...addFormData, carrier_name: e.target.value })}
                  disabled={addLoading}
                  placeholder="e.g., ABC Trucking LLC"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid #d3ccbc',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  MC Number
                </label>
                <input
                  type="text"
                  value={addFormData.mc_number}
                  onChange={(e) => setAddFormData({ ...addFormData, mc_number: e.target.value })}
                  disabled={addLoading}
                  placeholder="e.g., MC-123456"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid #d3ccbc',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  DNU Date *
                </label>
                <input
                  type="date"
                  value={addFormData.dnu_date}
                  onChange={(e) => setAddFormData({ ...addFormData, dnu_date: e.target.value })}
                  disabled={addLoading}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid #d3ccbc',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  Notes
                </label>
                <textarea
                  value={addFormData.notes}
                  onChange={(e) => setAddFormData({ ...addFormData, notes: e.target.value })}
                  disabled={addLoading}
                  placeholder="Optional notes about this DNU entry..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid #d3ccbc',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              {addError && (
                <div
                  style={{
                    padding: '12px',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: '1rem',
                    background: '#f2dad2',
                    border: '1px solid #b3412c',
                    color: '#7a2b1e',
                    fontSize: '0.875rem'
                  }}
                >
                  {addError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary"
                  disabled={addLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCarrier}
                  className="btn-primary"
                  disabled={addLoading}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {addLoading ? (
                    <>
                      <LoadingSpinner />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus size={16} />
                      Add to DNU
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {showImportModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: "var(--z-modal)",
              padding: '20px'
            }}
            onClick={() => {
              if (!importing) {
                setShowImportModal(false);
                setImportResult(null);
                setSelectedFile(null);
              }
            }}
          >
            <div
              className="card-modern"
              style={{
                maxWidth: '500px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="heading-2" style={{ margin: 0 }}>Import DNU List</h2>
                <button
                  onClick={() => {
                    if (!importing) {
                      setShowImportModal(false);
                      setImportResult(null);
                      setSelectedFile(null);
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: '#857f73'
                  }}
                  disabled={importing}
                >
                  <X size={20} />
                </button>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  Shipper
                </label>
                <select
                  value={importShipper}
                  onChange={(e) => setImportShipper(e.target.value)}
                  disabled={importing}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid #d3ccbc',
                    fontSize: '0.875rem'
                  }}
                >
                  {availableShippers.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  DNU Date (when carriers were added to DNU list)
                </label>
                <input
                  type="date"
                  value={importDnuDate}
                  onChange={(e) => setImportDnuDate(e.target.value)}
                  disabled={importing}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid #d3ccbc',
                    fontSize: '0.875rem'
                  }}
                />
                <p style={{ marginTop: '4px', fontSize: '0.75rem', color: '#857f73' }}>
                  This date will be used if not specified in the file or filename
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  Excel File (.xlsx or .xls)
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  disabled={importing}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid #d3ccbc',
                    fontSize: '0.875rem'
                  }}
                />
                {selectedFile && (
                  <p style={{ marginTop: '8px', fontSize: '0.875rem', color: '#857f73' }}>
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              <div
                style={{
                  background: 'var(--color-gray-100)',
                  border: '1px solid var(--color-gray-400)',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px',
                  marginBottom: '1.5rem'
                }}
              >
                <p style={{ fontSize: '0.875rem', color: 'var(--color-gray-700)', margin: 0, lineHeight: '1.6' }}>
                  <strong>Required Columns:</strong> DOT Number, Carrier Name, MC Number
                  <br />
                  <strong>Date Handling:</strong> Include a "DNU Date" or "Date Added" column for each carrier's original DNU date.
                  If not provided, the filename date will be used (e.g., "USPS DNU List (2.1.25).xlsx" → 2/1/2025).
                </p>
              </div>

              {importResult && (
                <div
                  style={{
                    padding: '12px',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: '1.5rem',
                    background: importResult.success ? '#d1e6d9' : '#f2dad2',
                    border: `1px solid ${importResult.success ? '#5d7148' : '#b3412c'}`,
                    color: importResult.success ? '#4b5c3a' : '#7a2b1e'
                  }}
                >
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600' }}>
                    {importResult.message}
                  </p>
                  {importResult.errors && importResult.errors.length > 0 && (
                    <details style={{ marginTop: '8px', fontSize: '0.75rem' }}>
                      <summary style={{ cursor: 'pointer', fontWeight: '600' }}>
                        {importResult.errors.length} errors occurred
                      </summary>
                      <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                        {importResult.errors.slice(0, 10).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                        {importResult.errors.length > 10 && (
                          <li>... and {importResult.errors.length - 10} more</li>
                        )}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportResult(null);
                    setSelectedFile(null);
                  }}
                  className="btn-secondary"
                  disabled={importing}
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportSubmit}
                  className="btn-primary"
                  disabled={importing || !selectedFile}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {importing ? (
                    <>
                      <LoadingSpinner />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      Import
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DnuAnalyticsPage;
